import { LinkText } from '@/components/atoms';
import CustomDataTable from '@/components/organisms/CustomDataTable/CustomDataTable.jsx';
import { getCellDataMappedCols } from '@/helpers/dataTableCellDataMapper';
import { useBulkModifiers } from '@/hooks/useBulkModifiers';
import { useAppDispatch } from '@/store';
import { resetBulkModifiers } from '@/store/slices/bulkPointSlice';
import { getSelectedRowData } from '@/utils/commons';
import { ROUTES } from '@/utils/constants/routesConstants';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const cellDataMapperObj = navigate => ({
  bulkModifierName: {
    cellData: ({ row: { original } = {} }) => (
      <LinkText
        text={original?.bulkModifierName}
        id={original?.bulkModifierName + ' ' + (original?.id || original?.uuid)}
        handleClick={() => {
          if (original?.status === 'draft' || !original?.id) {
            navigate(`${ROUTES.BULK_POINT_MODIFIER_VIEW.replace(':id', original?.uuid)}`, {
              state: {
                uuid: original?.uuid,
                formData: original
              }
            });
          } else {
            navigate(`${ROUTES.BULK_POINT_MODIFIER_VIEW.replace(':id', original?.id)}`, {
              state: { id: original.id }
            });
          }
        }}
      />
    ),
    linkHandler: (row) => {
      if (row?.status === 'draft' || !row?.id) {
        navigate(`${ROUTES.BULK_POINT_MODIFIER_VIEW.replace(':id', row?.uuid)}`, {
          state: {
            uuid: row?.uuid,
            formData: row
          }
        });
      } else {
        navigate(`${ROUTES.BULK_POINT_MODIFIER_VIEW.replace(':id', row?.id)}`, {
          state: { id: row.id }
        });
      }
    },
    linkLabel: (row) => row.bulkModifierName || '—',
  }
})

const BulkModifiersList = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [rowSelected, setRowSelected] = useState([]);
  const [isSelectAll, setIsSelectAll] = useState(false);
  const {
    bulkModifiersData,
    bulkModifiersColConfigData,
    pageIndex,
    setPageIndex,
    setSortingObj,
    setSearchArry,
    isLoading,
    searchArr,
    sortingObj,
    handleResetAll,
    onDownloadBtnClick
  } = useBulkModifiers();

  const cellDataMappedCols = getCellDataMappedCols(cellDataMapperObj(navigate), bulkModifiersColConfigData);

  useEffect(() => {
    setRowSelected([]);
    setIsSelectAll(false);
  }, [searchArr, sortingObj]);

  // Keep rowSelected in sync with visible records when select all is active
  useEffect(() => {
    if (isSelectAll && bulkModifiersData?.data) {
      setRowSelected(bulkModifiersData.data);
    }
  }, [isSelectAll, bulkModifiersData?.data]);

  const onDownloadClick = async (selectedRows) => {
    // selectedRows is empty array when select all is active (passed from CustomDataTable)
    // Otherwise it contains the selected row objects
    const ids = selectedRows?.length > 0 
      ? selectedRows.map(item => item?.id || item?.uuid)
      : [];
    onDownloadBtnClick(ids)
  } 
  
  const handleResetFormData = () => {
    dispatch(resetBulkModifiers());
    navigate(ROUTES.BULK_POINT_MODIFIER_CREATE);
  };

  return (
    <div className="bulkModifiersDataTable w-full">
      <CustomDataTable
        columns={cellDataMappedCols}
        records={bulkModifiersData?.data || []}
        isLoading={isLoading}
        pageSize={10}
        totalRecords={bulkModifiersData?.totalRecords || 0}
        setPageIndex={setPageIndex}
        handleSorting={setSortingObj}
        setSearchArry={setSearchArry}
        onResetAll={handleResetAll}
        onRowSelect={(selectedRecords, isAll) => {
          setRowSelected(selectedRecords);
          setIsSelectAll(isAll);
        }}
        isSelectAll={isSelectAll}
        onDownload={onDownloadClick}
        showDownload={true}
        onNameClick={getSelectedRowData}
        pageIndex={pageIndex}
        showCreateNew={true}
        onCreateNew={handleResetFormData}
        initialSortConfig={sortingObj}
      />
      {bulkModifiersData?.totalRecords > 0 && (
        <div className="txtRightAlign">{bulkModifiersData?.totalRecords} total items</div>
      )}
    </div>
  );
};

export default BulkModifiersList;
